// Modèle musical commun. Il sert aujourd'hui aux notes descendantes et à l'appariement,
// et demain à l'affichage de la partition et à la vue combinée.
// Le temps musical est exprimé en ticks (PPQ = 480 ticks par noire).

export const PPQ = 480;

export type Hand = 'right' | 'left';
export const HANDS: readonly Hand[] = ['right', 'left'];

export interface Note {
  id: string;
  /** Hauteur MIDI (60 = do central). */
  pitch: number;
  /** Position de départ en ticks depuis le début du morceau. */
  start: number;
  /** Durée en ticks. */
  duration: number;
  hand: Hand;
  /** Doigté 1 (pouce) à 5 (auriculaire), facultatif. */
  finger?: number;
}

export interface Part {
  hand: Hand;
  clef: 'treble' | 'bass';
  notes: Note[];
}

export interface TimeSignature {
  /** Nombre de temps par mesure (le chiffre du haut). */
  beats: number;
  /** Unité de temps (le chiffre du bas : 4 = noire, 8 = croche). */
  beatUnit: number;
}

export interface Score {
  id: string;
  title: string;
  subtitle?: string;
  author: string;
  license: string;
  kind: 'exercise' | 'piece';
  level: 1 | 2 | 3 | 4 | 5;
  tags: string[];
  description: string;
  ppq: number;
  timeSignature: TimeSignature;
  /** Armure en quintes (0 = do majeur, 1 = sol majeur, -1 = fa majeur). Sert à la partition. */
  keyFifths: number;
  /** Tempo cible, en noires par minute. */
  tempo: number;
  /** Tempo minimal proposé par l'interface. */
  minTempo: number;
  parts: Part[];
}

/** Un pas d'appariement : toutes les notes qui démarrent au même tick. */
export interface Step {
  index: number;
  tick: number;
  notes: Note[];
}

export function ticksPerBeat(score: Score): number {
  return (score.ppq * 4) / score.timeSignature.beatUnit;
}

export function ticksPerMeasure(score: Score): number {
  return ticksPerBeat(score) * score.timeSignature.beats;
}

export function allNotes(score: Score, hands: readonly Hand[] = HANDS): Note[] {
  const notes = score.parts.filter((p) => hands.includes(p.hand)).flatMap((p) => p.notes);
  return notes.slice().sort((a, b) => a.start - b.start || a.pitch - b.pitch);
}

export function totalTicks(score: Score, hands: readonly Hand[] = HANDS): number {
  let end = 0;
  for (const n of allNotes(score, hands)) end = Math.max(end, n.start + n.duration);
  return end;
}

export function measureCount(score: Score): number {
  const tpm = ticksPerMeasure(score);
  return Math.max(1, Math.ceil(totalTicks(score) / tpm));
}

export function buildSteps(score: Score, hands: readonly Hand[] = HANDS): Step[] {
  const byTick = new Map<number, Note[]>();
  for (const n of allNotes(score, hands)) {
    const list = byTick.get(n.start);
    if (list) list.push(n);
    else byTick.set(n.start, [n]);
  }
  return [...byTick.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([tick, notes], index) => ({ index, tick, notes }));
}

export function pitchRange(score: Score, hands: readonly Hand[] = HANDS): { min: number; max: number } {
  const notes = allNotes(score, hands);
  if (notes.length === 0) return { min: 60, max: 72 };
  let min = Infinity;
  let max = -Infinity;
  for (const n of notes) {
    if (n.pitch < min) min = n.pitch;
    if (n.pitch > max) max = n.pitch;
  }
  return { min, max };
}

/** Un morceau contient-il des notes simultanées dans une même main (accords) ? */
export function hasChords(score: Score, hands: readonly Hand[] = HANDS): boolean {
  for (const part of score.parts) {
    if (!hands.includes(part.hand)) continue;
    const starts = new Set<number>();
    for (const n of part.notes) {
      if (starts.has(n.start)) return true;
      starts.add(n.start);
    }
  }
  return false;
}

/** Conversion ticks → millisecondes pour un tempo donné (noires par minute). */
export function ticksToMs(ticks: number, tempo: number, ppq: number = PPQ): number {
  return (ticks / ppq) * (60000 / tempo);
}

export function msToTicks(ms: number, tempo: number, ppq: number = PPQ): number {
  return (ms / 60000) * tempo * ppq;
}

export function measureOfTick(score: Score, tick: number): number {
  return Math.floor(tick / ticksPerMeasure(score));
}

/** Vérifications de cohérence d'un morceau (utilisées par les tests et, plus tard, l'import). */
export function validateScore(score: Score): string[] {
  const problems: string[] = [];
  const ids = new Set<string>();
  if (!score.id) problems.push('identifiant manquant');
  if (score.ppq <= 0) problems.push('ppq invalide');
  if (score.tempo <= 0 || score.minTempo <= 0 || score.minTempo > score.tempo) problems.push('tempo invalide');
  if (score.parts.length === 0) problems.push('aucune partie');
  const seenHands = new Set<Hand>();
  for (const part of score.parts) {
    if (seenHands.has(part.hand)) problems.push(`main ${part.hand} définie deux fois`);
    seenHands.add(part.hand);
    for (const n of part.notes) {
      if (ids.has(n.id)) problems.push(`identifiant de note en double : ${n.id}`);
      ids.add(n.id);
      if (n.hand !== part.hand) problems.push(`note ${n.id} rattachée à la mauvaise main`);
      if (!Number.isInteger(n.pitch) || n.pitch < 21 || n.pitch > 108) problems.push(`note ${n.id} hors du clavier`);
      if (n.start < 0 || !Number.isInteger(n.start)) problems.push(`note ${n.id} : position invalide`);
      if (n.duration <= 0 || !Number.isInteger(n.duration)) problems.push(`note ${n.id} : durée invalide`);
      if (n.finger !== undefined && (n.finger < 1 || n.finger > 5)) problems.push(`note ${n.id} : doigté invalide`);
    }
  }
  return problems;
}
